import {
  useContext,
  useEffect,
  useState,
} from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { useDebounce } from 'use-debounce';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Chip, { ChipProps } from '@mui/material/Chip';
import RefreshIcon from '@mui/icons-material/Refresh';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { useGetRemindersQuery } from '#services/order';
import { formatDate, startOfDayInTZ, endOfDayInTZ } from '#common/utils';
import { FeedbackUIContext } from '#context/feedback-ui-context';

import { normalizeStartOfDay, normalizeEndOfDay } from './reminder.utils';

interface DateRange {
  from: Date | null;
  to: Date | null;
}

interface OrderRef {
  id?: string | number;
  createdDate?: string;
  nextEstimatedShipDate?: string;
  [key: string]: unknown;
}

interface Reminder {
  _id: string;
  name: string;
  description?: string;
  criticalLevel: string;
  reminderDate: string;
  createdByName?: string;
  createdAt: string;
  orderId?: string | number;
  order?: OrderRef;
}

interface OrderGroup {
  order?: OrderRef | undefined;
  reminders: Reminder[];
}

const CRITICAL_LEVELS = {
  ALL: '',
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
};

const CRITICAL_LEVEL_COLORS: Record<string, string> = {
  [CRITICAL_LEVELS.LOW]: 'default',
  [CRITICAL_LEVELS.MEDIUM]: 'warning',
  [CRITICAL_LEVELS.HIGH]: 'error',
};

const CRITICAL_LEVEL_LABELS: Record<string, string> = {
  [CRITICAL_LEVELS.ALL]: 'All Levels',
  [CRITICAL_LEVELS.LOW]: 'Low',
  [CRITICAL_LEVELS.MEDIUM]: 'Medium',
  [CRITICAL_LEVELS.HIGH]: 'High',
};

const getDefaultDateRange = (timeZone?: string): DateRange => {
  // "today" means the market's calendar day, so anchor on the instant
  const thirtyDaysFromNow = new Date();
  thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

  return {
    from: startOfDayInTZ(timeZone),
    to: endOfDayInTZ(timeZone, thirtyDaysFromNow),
  };
};

export default function Reminders() {
  const navigate = useNavigate();
  const { showBackdrop, hideBackdrop } = useContext(FeedbackUIContext)!;
  const marketTimeZone = useSelector(
    (state: { appConfig: { timeZone?: string } }) => state.appConfig?.timeZone,
  );

  const [criticalLevel, setCriticalLevel] = useState(CRITICAL_LEVELS.ALL);
  const [dateRange, setDateRange] = useState<DateRange>(() => getDefaultDateRange(marketTimeZone));
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);

  const dateFromIso = dateRange.from ? dateRange.from.toISOString() : undefined;
  const dateToIso = dateRange.to ? dateRange.to.toISOString() : undefined;

  const {
    data: remindersData, isLoading, isFetching, error, refetch,
  } = useGetRemindersQuery({
    criticalLevel: criticalLevel || undefined,
    dateFrom: dateFromIso,
    dateTo: dateToIso,
    limit: 200,
  }, {
    refetchOnMountOrArgChange: true,
  });

  const reminders = (remindersData as { reminders?: Reminder[] } | undefined)?.reminders || [];

  // Filter by search term on client side
  const filteredReminders = searchTerm
    ? reminders.filter((r) => {
      const searchLower = debouncedSearchTerm.toLowerCase();
      return (
        r.name.toLowerCase().includes(searchLower)
        || r.description?.toLowerCase().includes(searchLower)
        || r.order?.id?.toString().includes(searchLower)
      );
    })
    : reminders;

  const loading = isLoading || isFetching;

  useEffect(() => {
    if (loading) {
      showBackdrop();
    } else {
      hideBackdrop();
    }

    return () => {
      hideBackdrop();
    };
  }, [loading, showBackdrop, hideBackdrop]);

  const handleDateChange = (
    key: 'from' | 'to',
    value: Date | null,
    context: { validationError?: unknown } | undefined,
  ) => {
    if (value && context?.validationError) {
      return;
    }
    setDateRange((prev) => {
      let nextValue = value instanceof Date && !Number.isNaN(value.getTime()) ? value : null;
      if (!nextValue && value === null) {
        nextValue = null;
      }
      if (nextValue) {
        nextValue = (key === 'from')
          ? normalizeStartOfDay(nextValue, marketTimeZone)
          : normalizeEndOfDay(nextValue, marketTimeZone);
      }
      const updated: DateRange = { ...prev, [key]: nextValue };
      if (updated.from && updated.to && updated.from > updated.to) {
        if (key === 'from') {
          updated.to = nextValue;
        } else {
          updated.from = nextValue;
        }
      }
      return updated;
    });
  };

  const handleResetFilters = () => {
    setCriticalLevel(CRITICAL_LEVELS.ALL);
    setDateRange(getDefaultDateRange(marketTimeZone));
    setSearchTerm('');
  };

  const buildOrderDetailUrl = (orderId?: string | number) => `/management/order/detail?orderId=${orderId}`;

  // Group reminders by order
  const remindersByOrder = filteredReminders.reduce<Record<string, OrderGroup>>((acc, reminder) => {
    const orderId = String(reminder.orderId);
    if (!acc[orderId]) {
      acc[orderId] = {
        order: reminder.order,
        reminders: [],
      };
    }
    acc[orderId].reminders.push(reminder);
    return acc;
  }, {});

  const orderGroups = Object.values(remindersByOrder);

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        sx={{
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'flex-start' },
          mb: 2,
        }}
      >
        <Box>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
              mb: 1,
            }}
          >
            <NotificationsActiveIcon color="primary" fontSize="large" />
            <Typography variant="h5">
              Order Reminders
            </Typography>
          </Stack>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            View and manage all order reminders. Use filters to find specific reminders.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={() => { void navigate('/management/order/list'); }}
          sx={{ mt: { xs: 1, md: 0 } }}
        >
          Back to Customer Orders
        </Button>
      </Stack>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          sx={{
            alignItems: { xs: 'stretch', lg: 'center' },
          }}
        >
          <TextField
            size="small"
            label="Search"
            placeholder="Search by name, description, order #, or customer"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1 }}
          />
          <TextField
            select
            size="small"
            label="Critical Level"
            value={criticalLevel}
            onChange={(e) => setCriticalLevel(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            {Object.entries(CRITICAL_LEVEL_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <DatePicker
                label="Due Date from"
                value={dateRange.from}
                onChange={(date, ctx) => handleDateChange('from', date, ctx)}
                slotProps={{ textField: { size: 'small' } }}
              />
              <DatePicker
                label="Due Date to"
                value={dateRange.to}
                onChange={(date, ctx) => handleDateChange('to', date, ctx)}
                slotProps={{ textField: { size: 'small' } }}
              />
            </Stack>
          </LocalizationProvider>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{
              alignItems: 'center',
              mt: '-8px !important',
            }}
          >
            <Button
              variant="outlined"
              size="small"
              sx={{ height: 40 }}
              onClick={handleResetFilters}
            >
              Reset
            </Button>
            <Tooltip title="Refresh reminders">
              <span>
                <IconButton
                  color="primary"
                  onClick={() => { void refetch(); }}
                  disabled={loading}
                >
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {filteredReminders.length}
              {' '}
              {filteredReminders.length === 1 ? 'reminder' : 'reminders'}
              {' '}
              found
              {searchTerm && ' (filtered)'}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              Showing reminders for
              {' '}
              {orderGroups.length}
              {' '}
              {orderGroups.length === 1 ? 'order' : 'orders'}
            </Typography>
          </Box>
        </Stack>
        <Divider sx={{ mb: 2 }} />
        {loading ? (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            Loading reminders...
          </Typography>
        ) : error ? (
          <Typography variant="body2" color="error">
            Unable to load reminders. Please try again later.
          </Typography>
        ) : orderGroups.length ? (
          <Stack spacing={2}>
            {orderGroups.map((group) => {
              const { order, reminders: orderReminders } = group;
              const detailUrl = buildOrderDetailUrl(order?.id);
              return (
                <Box
                  key={order?.id || 'unknown'}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                  }}
                >
                  <Stack
                    direction="row"
                    sx={{
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Order #
                        {order?.id || 'N/A'}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        Created:
                        {' '}
                        {order?.createdDate ? formatDate(order.createdDate) : '—'}
                        {order?.nextEstimatedShipDate && (
                          <>
                            {' · '}
                            ETA:
                            {' '}
                            {formatDate(order.nextEstimatedShipDate)}
                          </>
                        )}
                      </Typography>
                    </Box>
                    <Link
                      href={detailUrl}
                      underline="hover"
                      variant="body2"
                      onClick={(e) => {
                        e.preventDefault();
                        void navigate(detailUrl);
                      }}
                    >
                      View Order
                    </Link>
                  </Stack>
                  <Stack spacing={1}>
                    {orderReminders.map((reminder) => (
                      <Box
                        key={reminder._id}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 1.5,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{
                            alignItems: 'center',
                            mb: 0.5,
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {reminder.name}
                          </Typography>
                          <Chip
                            label={CRITICAL_LEVEL_LABELS[reminder.criticalLevel]}
                            size="small"
                            color={CRITICAL_LEVEL_COLORS[reminder.criticalLevel] as ChipProps['color']}
                          />
                        </Stack>
                        {reminder.description && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.secondary',
                              mb: 0.5,
                            }}
                          >
                            {reminder.description}
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          Due:
                          {' '}
                          {formatDate(reminder.reminderDate)}
                          {' · '}
                          Created by
                          {' '}
                          {reminder.createdByName || '-'}
                          {' on '}
                          {formatDate(reminder.createdAt)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            No reminders found for the selected filters.
          </Typography>
        )}
      </Paper>
    </Box>
  );
}
